// Enhanced MessageLog.js - FIXED for real-time updates with toggle read/unread
import React, { useEffect, useState, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  writeBatch,
  doc as firestoreDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import '../styles/MessageLog.css';

function FilePreviewModal({ attachment, onClose }) {
  const [textContent, setTextContent] = useState('');
  const { url, name, contentType = '', ext = '' } = attachment || {};

  useEffect(() => {
    if (!attachment) return;
    if (isTextLike(contentType, ext)) {
      fetch(url)
        .then(r => r.text())
        .then(setTextContent)
        .catch(() => setTextContent('Unable to load text preview.'));
    } else {
      setTextContent('');
    }
  }, [attachment, contentType, ext, url]);

  if (!attachment) return null;

  const renderBody = () => {
    const ct = (contentType || '').toLowerCase();
    const e = (ext || '').toLowerCase();

    const imgExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const vidExts = ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v'];
    const audExts = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac'];

    const isImg = ct.startsWith('image/') || imgExts.includes(e);
    const isPdf = ct === 'application/pdf' || e === 'pdf';
    const isVid = ct.startsWith('video/') || vidExts.includes(e);
    const isAud = ct.startsWith('audio/') || audExts.includes(e);

    const forceDownload = () => {
      try {
        if (typeof handleDownload === 'function') {
          handleDownload({ url, name, contentType: ct, ext: e });
          return;
        }
      } catch {}
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    if (isImg) {
      return <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />;
    }

    if (isPdf) {
      return (
        <iframe
          title={name}
          src={url}
          style={{ width: '90vw', height: '75vh', border: 'none' }}
        />
      );
    }

    if (isVid) {
      return <video src={url} controls style={{ maxWidth: '90vw', maxHeight: '75vh' }} />;
    }

    if (isAud) {
      return <audio src={url} controls style={{ width: '80%' }} />;
    }

    if (isHtml(ct, e)) {
      return (
        <iframe
          title={name}
          src={url}
          sandbox=""
          style={{ width: '90vw', height: '75vh', border: 'none', background: '#fff' }}
        />
      );
    }

    if (isTextLike(ct, e)) {
      return (
        <pre
          style={{
            maxWidth: '90vw',
            maxHeight: '75vh',
            overflow: 'auto',
            background: '#0f172a',
            color: '#e2e8f0',
            padding: 16,
            borderRadius: 8,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {textContent}
        </pre>
      );
    }

    return (
      <div style={{ padding: 12 }}>
        <p>Preview not available for this file type.</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={forceDownload}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 6 }}
          >
            Download
          </button>
          <button
            onClick={() => handleDownload({ url, name, contentType, ext })}
            style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 6 }}
          >
            Download
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', 
        inset: 0, 
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        zIndex: 9999, 
        padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', 
          borderRadius: 12, 
          padding: 16,
          maxWidth: '95vw', 
          maxHeight: '85vh',
          display: 'flex', 
          flexDirection: 'column', 
          gap: 12
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 14 }}>{name}</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleDownload({ url, name, contentType, ext })}
              style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 6 }}
            >
              Download
            </button>
            <button
              onClick={onClose}
              style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e0e0e0', borderRadius: 6 }}
            >
              Close
            </button>
          </div>
        </div>
        <div style={{ overflow: 'auto' }}>
          {renderBody()}
        </div>
      </div>
    </div>
  );
}

// --- attachment helpers ---
const typeStartsWith = (ct = '', prefix = '') => (ct || '').toLowerCase().startsWith(prefix);

const isTextLike = (ct = '', ext = '') => {
  ct = (ct || '').toLowerCase();
  ext = (ext || '').toLowerCase();
  return (
    ct.startsWith('text/') ||
    ct === 'application/json' ||
    ['txt', 'json', 'csv', 'md', 'log'].includes(ext)
  );
};

const isHtml = (ct = '', ext = '') => {
  ct = (ct || '').toLowerCase();
  ext = (ext || '').toLowerCase();
  return ct === 'text/html' || ext === 'html' || ext === 'htm';
};

const renderTextWithLinks = (text) => {
  if (!text) return text;
  
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.(?:com|org|net|edu|gov|mil|biz|info|mobi|name|aero|jobs|museum|travel|[a-z]{2})(?:\/[^\s]*)?|bit\.ly\/[^\s]+|tinyurl\.com\/[^\s]+|t\.co\/[^\s]+|youtu\.be\/[^\s]+)/gi;
  
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    urlRegex.lastIndex = 0;
    if (urlRegex.test(part)) {
      let href = part;
      if (!part.startsWith('http')) {
        href = `https://${part}`;
      }
      
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#ffa600ff',
            textDecoration: 'underline',
            wordBreak: 'break-all'
          }}
          onClick={(e) => e.stopPropagation()} 
        >
          {part}
        </a>
      );
    }
    
    return part;
  });
};

const handleDownload = async (a) => {
  try {
    const res = await fetch(a.url, { mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = a.name || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  } catch (e) {
    window.open(a.url, '_blank', 'noopener,noreferrer');
  }
};

export default function MessageLog({ 
  clientId, 
  recipientType, 
  clearFlag = 0,
  orderId = null,           
  showOrderMessages = null  
}) {
  const [messages, setMessages] = useState([]);
  const [isClearing, setIsClearing] = useState(false);
  const [filterMode, setFilterMode] = useState(showOrderMessages);
  const [updatingReadStatus, setUpdatingReadStatus] = useState(new Set());
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const containerRef = useRef(null);
  const auth = getAuth();

  // Pull user role and name from AuthContext - already fetched at login, no extra Firestore call
  const { currentUser, userData } = useAuth();
  const currentUserRole = userData?.role || 'client';
  const currentUserName = userData?.name || userData?.displayName || currentUser?.displayName || 'Unknown User';

  // Clear messages when clearFlag changes
  useEffect(() => {
    if (!clientId || clearFlag === 0) return;

    const clearMessages = async () => {
      setIsClearing(true);
      
      try {
        const msgsCol = collection(db, 'clients', clientId, 'messages');
        let delQ;
        
        if (recipientType && orderId) {
          delQ = query(
            msgsCol, 
            where('recipientType', '==', recipientType),
            where('orderId', '==', orderId)
          );
        } else if (recipientType) {
          delQ = query(msgsCol, where('recipientType', '==', recipientType));
        } else if (orderId) {
          delQ = query(msgsCol, where('orderId', '==', orderId));
        } else {
          delQ = query(msgsCol);
        }

        const snap = await getDocs(delQ);
        if (!snap.empty) {
          const batch = writeBatch(db);
          snap.docs.forEach(d => {
            const path = firestoreDoc(db, 'clients', clientId, 'messages', d.id);
            batch.delete(path);
          });
          await batch.commit();
          console.log(`Cleared ${snap.docs.length} messages for ${recipientType}${orderId ? ` order ${orderId}` : ''}`);
        }

        setMessages([]);
      } catch (error) {
        console.error('Error clearing messages:', error);
      } finally {
        setTimeout(() => {
          setIsClearing(false);
        }, 500);
      }
    };

    clearMessages();
  }, [clearFlag, clientId, recipientType, orderId]);

  // Load messages with real-time updates
  useEffect(() => {
    if (!clientId || isClearing) return;

    const msgsCol = collection(db, 'clients', clientId, 'messages');
    const q = query(msgsCol, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, snap => {
      if (isClearing) return;

      let data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      if (recipientType === 'internal') {
        data = data.filter(msg => msg.recipientType === 'internal');
      } else if (recipientType === 'client') {
        data = data.filter(msg => {
          return !msg.recipientType || 
                 msg.recipientType === 'client' || 
                 msg.recipientType === 'admin' ||
                 msg.senderRole === 'client';
        });
      }

      if (orderId) {
        data = data.filter(msg => msg.orderId === orderId);
      }

      if (filterMode === true) {
        data = data.filter(msg => msg.isOrderMessage || msg.orderId);
      } else if (filterMode === false) {
        data = data.filter(msg => !msg.isOrderMessage && !msg.orderId);
      }
      
      setMessages(data);

      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      }, 100);

    }, error => {
      console.error('MessageLog subscription error:', error);
    });

    return unsubscribe;
  }, [clientId, recipientType, isClearing, orderId, filterMode]);

  const bubbleSide = msg => {
    if (currentUserRole === 'client') {
      return msg.senderRole === 'client' ? 'mine' : 'yours';
    } else {
      return msg.senderId === auth.currentUser?.uid ? 'mine' : 'yours';
    }
  };

  // Toggle read function that tracks who read/unread the message
  const toggleMessageRead = async (messageId) => {
    const message = messages.find(msg => msg.id === messageId);
    if (!message || updatingReadStatus.has(messageId)) return;

    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      setUpdatingReadStatus(prev => new Set([...prev, messageId]));
      
      const now = Timestamp.fromDate(new Date());
      const ref = firestoreDoc(db, 'clients', clientId, 'messages', messageId);
      
      const updateData = {
        read: !message.read,
        readAt: !message.read ? now : null,
        readBy: !message.read ? currentUserName : null,
        readById: !message.read ? currentUser.uid : null,
      };

      await updateDoc(ref, updateData);

      console.log(`${!message.read ? 'Marked' : 'Unmarked'} message ${messageId} as ${!message.read ? 'read' : 'unread'} by ${currentUserName}`);

    } catch (err) {
      console.error('Could not toggle message read status:', err);
    } finally {
      setTimeout(() => {
        setUpdatingReadStatus(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }, 500);
    }
  };

  const markAllAsRead = async () => {
    if (!clientId || messages.length === 0) {
      console.log('No messages to mark as read');
      return;
    }

    const unreadMessages = messages.filter(msg => !msg.read);
    if (unreadMessages.length === 0) {
      console.log('All messages are already read');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const batch = writeBatch(db);
      const now = Timestamp.fromDate(new Date());

      unreadMessages.forEach(msg => {
        const ref = firestoreDoc(db, 'clients', clientId, 'messages', msg.id);
        batch.update(ref, { 
          read: true, 
          readAt: now,
          readBy: currentUserName,
          readById: currentUser.uid
        });
      });

      await batch.commit();
      console.log(`Marked ${unreadMessages.length} messages as read by ${currentUserName}`);

    } catch (err) {
      console.error('Could not mark all as read:', err);
    }
  };

  const getRecipientInfo = (msg) => {
    if (msg.specificRecipientName) {
      return ` → ${msg.specificRecipientName}`;
    }
    if (msg.recipientType === 'internal') {
      return ' → Internal Team';
    }
    if (msg.recipientType === 'admin') {
      return ' → All Admins';
    }
    return '';
  };

  const getNormalizedAttachments = (msg) => {
    // New format
    if (Array.isArray(msg.attachments) && msg.attachments.length > 0) {
      return msg.attachments.map(a => {
        const nameExt = (a?.name?.includes('.') ? a.name.split('.').pop() : '').toLowerCase();
        const ct = (a.contentType || a.type || msg.attachmentType || '').toLowerCase();
        return {
          url: a.url,
          name: a.name,
          contentType: ct,
          ext: (a.ext || nameExt),
          size: a.size || null,
          category: a.category || '',
          isImage: a.isImage ?? (ct ? ct.startsWith('image/') : false),
        };
      });
    }

    // Legacy single attachment fields
    if (msg.attachmentUrl) {
      const name = msg.attachmentName || 'Attachment';
      const nameExt = (name.includes('.') ? name.split('.').pop() : '').toLowerCase();
      const ct = (msg.attachmentType || '').toLowerCase();
      return [{
        url: msg.attachmentUrl,
        name,
        contentType: ct,
        ext: nameExt,
        size: null,
        category: '',
        isImage: msg.isImage ?? (ct ? ct.startsWith('image/') : false),
      }];
    }

    return [];
  };

  // Get count of different message types
  const getMessageCounts = () => {
    const total = messages.length;
    const unread = messages.filter(m => !m.read).length;
    const orderMessages = messages.filter(m => m.isOrderMessage || m.orderId).length;
    const generalMessages = total - orderMessages;
    
    return { total, unread, orderMessages, generalMessages };
  };

  const { total, unread, orderMessages, generalMessages } = getMessageCounts();

  return (
    <div className="chat" ref={containerRef} style={{ overflowY: 'auto', height: '400px' }}>
      <div className="messages">
        {/* Header with controls */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '8px',
          borderBottom: '1px solid #eee',
          marginBottom: '8px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            {unread} unread of {total} total
            {!orderId && (
              <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>
                ({orderMessages} order, {generalMessages} general)
              </span>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Filter toggle buttons */}
            {!orderId && (
              <>
                <button 
                  onClick={() => setFilterMode(null)}
                  style={{
                    backgroundColor: filterMode === null ? '#003cff' : '#f0f0f0',
                    color: filterMode === null ? 'white' : '#333',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  All
                </button>
                <button 
                  onClick={() => setFilterMode(true)}
                  style={{
                    backgroundColor: filterMode === true ? '#003cff' : '#f0f0f0',
                    color: filterMode === true ? 'white' : '#333',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  Order ({orderMessages})
                </button>
                <button 
                  onClick={() => setFilterMode(false)}
                  style={{
                    backgroundColor: filterMode === false ? '#003cff' : '#f0f0f0',
                    color: filterMode === false ? 'white' : '#333',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  General ({generalMessages})
                </button>
              </>
            )}
            
            <button 
              onClick={markAllAsRead} 
              className="mark-read-btn"
              disabled={unread === 0 || updatingReadStatus.size > 0}
              style={{
                backgroundColor: (unread === 0 || updatingReadStatus.size > 0) ? '#ccc' : '#003cff',
                color: 'white',
                border: 'none',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: (unread === 0 || updatingReadStatus.size > 0) ? 'not-allowed' : 'pointer',
                fontSize: '0.8rem'
              }}
            >
              {updatingReadStatus.size > 0 ? 'Updating...' : 'Mark All as Read'}
            </button>
          </div>
        </div>

        {isClearing ? (
          <div className="message yours last">
            <em>Clearing messages...</em>
          </div>
        ) : messages.length === 0 ? (
          <div className="message yours last">
            <em>No messages yet.</em>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isLast = i === messages.length - 1;
            const side = bubbleSide(msg);
            const isUnread = !msg.read;
            const isUpdating = updatingReadStatus.has(msg.id);
            const recipientInfo = getRecipientInfo(msg);
            
            return (
              <div
                key={msg.id}
                className="message-wrapper"
                style={{
                  display: 'flex',
                  justifyContent: side === 'mine' ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  marginBottom: '10px'
                }}
              >
                <div
                  className={`message ${side} ${isLast ? 'last' : ''}`}
                  style={{
                    maxWidth: '80%',
                    transition: 'all 0.3s ease',
                    backgroundColor: isUnread && side === 'yours' ? '#fff3cd' : undefined,
                    border: isUnread && side === 'yours' ? '2px solid #ffc107' : undefined,
                    opacity: isUpdating ? 0.7 : 1,
                    position: 'relative'
                  }}
                >
                  {/* Read/Unread Toggle Button */}
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    zIndex: 10
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMessageRead(msg.id);
                      }}
                      disabled={isUpdating}
                      style={{
                        backgroundColor: isUnread ? '#ffc107' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '2px 6px',
                        fontSize: '10px',
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        opacity: isUpdating ? 0.6 : 1,
                        minWidth: '50px',
                        fontWeight: 'bold'
                      }}
                      title={`Click to mark as ${isUnread ? 'read' : 'unread'}`}
                    >
                      {isUpdating ? '⟳' : (isUnread ? 'UNREAD - CLICK TO MARK READ' : 'READ')}
                    </button>
                  </div>

                  <p style={{ fontSize: '0.75rem', marginBottom: '4px', paddingRight: '60px' }}>
                    {msg.createdAt?.toDate().toLocaleString() || ''}
                  </p>
                  
                  {/* Order context badge */}
                  {(msg.isOrderMessage || msg.orderId) && (
                    <div style={{
                      display: 'inline-block',
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      padding: '2px 6px',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: '500',
                      marginBottom: '4px'
                    }}>
                      📋 {msg.orderNumber || 'Order'} {msg.campaignName && `- ${msg.campaignName}`}
                    </div>
                  )}
                  
                  <p>
                    <strong>{msg.senderName || 'Unknown'} ({msg.senderRole}){recipientInfo}:</strong> {renderTextWithLinks(msg.text)}
                  </p>

                  {/* Unified attachments UI */}
                  {(() => {
                    const att = getNormalizedAttachments(msg);
                    if (att.length === 0) return null;

                    return (
                      <div style={{ marginTop: '8px' }}>
                        {att.length > 1 && (
                          <p style={{ fontSize: '0.75rem', marginBottom: '8px', color: '#666' }}>
                            {att.length} attachment{att.length > 1 ? 's' : ''}:
                          </p>
                        )}

                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                          gap: '8px'
                        }}>
                          {att.map((a, idx) => {
                            const ext = (a.ext || '').toLowerCase();
                            const isImg = a.isImage || typeStartsWith(a.contentType, 'image/') || ['jpg','jpeg','png','gif','bmp','webp','svg'].includes(ext);
                            const isPdf = ext === 'pdf' || a.contentType === 'application/pdf';

                            return (
                              <div
                                key={idx}
                                style={{
                                  border: '1px solid #ddd',
                                  borderRadius: 6,
                                  padding: 8,
                                  background: '#f9f9f9',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 6
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {/* Thumbnail for images */}
                                {isImg && (
                                  <img
                                    src={a.url}
                                    alt={a.name}
                                    style={{
                                      width: '100%',
                                      height: 200,
                                      objectFit: 'contain',
                                      borderRadius: 4,
                                      border: '1px solid #ddd'
                                    }}
                                  />
                                )}

                                {/* Header row */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ fontSize: 20 }}>
                                    {isImg ? '🖼️' : isPdf ? '📄' : typeStartsWith(a.contentType, 'video/') ? '🎬' :
                                     typeStartsWith(a.contentType, 'audio/') ? '🎧' :
                                     isHtml(a.contentType, a.ext) ? '🌐' :
                                     isTextLike(a.contentType, a.ext) ? '📝' : '📎'}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', wordBreak: 'break-word', flex: 1 }}>
                                    {a.name}
                                  </div>
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button
                                    type="button"
                                    onClick={() => setPreviewAttachment(a)}
                                    style={{
                                      fontSize: '0.75rem',
                                      padding: '4px 8px',
                                      borderRadius: 4,
                                      border: '1px solid #b3d9ff',
                                      background: '#f0f8ff',
                                      color: '#007AFF',
                                      cursor: 'pointer',
                                      flex: 1
                                    }}
                                    title="Preview"
                                  >
                                    View
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleDownload(a)}  
                                    style={{
                                      fontSize: '0.75rem',
                                      padding: '4px 8px',
                                      borderRadius: 4,
                                      border: '1px solid #ddd',
                                      background: '#fff',
                                      color: '#007AFF',
                                      cursor: 'pointer',
                                      flex: 1
                                    }}
                                    title="Download"
                                  >
                                    Download
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Enhanced read status display with user info */}
                  {msg.read && msg.readAt && msg.readBy && !isUpdating && (
                    <p style={{ fontSize: '0.75rem', marginTop: '4px', color: '#28a745' }}>
                      ✓ Read by <strong>{msg.readBy}</strong> at {msg.readAt.toDate().toLocaleString()}
                    </p>
                  )}
                  
                </div>
              </div>
            );
          })
        )}
      </div>

      {previewAttachment && (
        <FilePreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

    </div>
  );
}